// SAM-199 follow-up — TIME picker for a chosen SERVICE on the DATE the operator
// already picked in HowLongControl (the shared "How long?" date control). This
// component no longer picks its own date — HowLongControl is the single source
// for that, per the product owner's report that the flow asked for the date
// twice. Reads the existing GET /services/schedule/{serviceId} endpoint via
// useServiceSchedule and looks up just the one date it was given.
//
// A 'day' service (cabanas, Maralia — day_schedule.py) has exactly one
// whole-day slot per date and no time-of-day to choose: this component
// collapses to a plain statement instead of a time picker for those.
//
// New component: Tailwind + tokens.css only (SAM-175 freeze).

import { useEffect } from 'react';
import { AlertCircle, AlertTriangle, CalendarDays, CheckCircle2, Loader2 } from 'lucide-react';
import { useServiceSchedule, type Service } from '../../../hooks/useServices';
import { DAY_SERVICE_TIME, filterAvailableSlots, isDayBookable, isDayScheduleDay, isDayService, isLocalFallbackSchedule, remainingCapacity } from './serviceBooking';
import { useT } from '../../../i18n/admin';

type Props = {
  service: Pick<Service, 'id' | 'type'> | null;
  date: string | null; // "YYYY-MM-DD", from HowLongControl
  selectedTime: string | null;
  onSelectTime: (time: string | null) => void;
};

export const ServiceScheduleSlotPicker = ({ service, date, selectedTime, onSelectTime }: Props) => {
  const { data: schedule, isLoading, isError } = useServiceSchedule(service?.id || null);
  const { t, fmt } = useT();
  const dayType = isDayService(service);
  const day = date ? schedule?.availability?.[date] : undefined;
  const dayEntryIsDayShaped = isDayScheduleDay(day);
  const bookable = isDayBookable(day);
  const slotsForDay = filterAvailableSlots(day);
  // SAM-199 follow-up — some 'day' services (e.g. the cabanas) don't come back with a
  // day-shaped schedule entry at all: the engine still hands them a `slots` array with
  // exactly one entry spanning the whole day. Asking the operator to "pick" that one
  // meaningless time is confusing (after/05 in the design-access screenshots) — treat a
  // day-type service with exactly one available slot the same as a day-shaped entry.
  const singleDaySlot = dayType && !dayEntryIsDayShaped && slotsForDay.length === 1 ? slotsForDay[0] : null;
  const wholeDayTime = dayEntryIsDayShaped ? DAY_SERVICE_TIME : singleDaySlot?.time;
  const isWholeDayBooking = dayEntryIsDayShaped || !!singleDaySlot;

  // A 'day' service (or a day-type service whose engine schedule only ever hands back
  // one whole-day slot) has nothing to pick — as soon as the date resolves to a
  // bookable day, its one implicit slot is selected automatically.
  useEffect(() => {
    if (isWholeDayBooking && bookable && wholeDayTime && selectedTime !== wholeDayTime) {
      onSelectTime(wholeDayTime);
    }
    if ((!bookable || !date) && selectedTime) {
      onSelectTime(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWholeDayBooking, bookable, date, wholeDayTime]);

  if (!service) return null;

  if (!date) {
    return (
      <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <CalendarDays size={12} /> {t('booking.slots.pickDate')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Loader2 size={12} className="animate-spin" /> {t('booking.slots.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--danger)' }}>
        <AlertCircle size={12} /> {t('booking.slots.loadError')}
      </div>
    );
  }

  const localFallback = isLocalFallbackSchedule(schedule);

  const warning = localFallback && (
    <div
      className="cap"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 8,
        color: 'var(--warn-fg)',
        background: 'var(--warn-bg)',
        border: '1px solid var(--warn-line)',
        borderRadius: 6,
        padding: '6px 10px',
      }}
    >
      <AlertTriangle size={12} />
      {t('booking.slots.localFallback')}
    </div>
  );

  // No availability at all for the chosen date — say so right here, next to the
  // date the operator already picked, not in a separate widget.
  if (!bookable) {
    return (
      <div>
        {warning}
        <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--danger)' }}>
          <CalendarDays size={12} /> {t('booking.slots.noneOnDate', { date: fmt.date(date) })}
        </div>
      </div>
    );
  }

  // 'day' service: one whole-day slot, nothing to pick — either the schedule entry
  // is day-shaped (opens/closes on the entry itself), or it's a day-type service
  // whose engine schedule collapsed to a single slot (singleDaySlot).
  if (isWholeDayBooking) {
    const opens = day?.opens ?? singleDaySlot?.time;
    const closes = day?.closes;
    return (
      <div>
        {warning}
        <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--success, #15803d)' }}>
          <CheckCircle2 size={12} />
          {t('booking.slots.fullDay', { date: fmt.date(date), hours: opens && closes ? ` (${opens}–${closes})` : '' })}
        </div>
      </div>
    );
  }

  const availableSlots = slotsForDay;

  return (
    <div>
      {warning}
      <div className="flex flex-wrap gap-1.5">
        {availableSlots.map((slot) => {
          const selected = slot.time === selectedTime;
          const remaining = remainingCapacity(slot);
          return (
            <button
              key={slot.time}
              type="button"
              onClick={() => onSelectTime(slot.time)}
              aria-pressed={selected}
              className="rounded-full px-2.5 py-1 text-xs"
              style={{
                border: selected ? '1px solid var(--tenant-primary, #111827)' : '1px solid var(--line)',
                background: selected ? 'var(--tenant-primary, #111827)' : 'var(--surface-2)',
                color: selected ? '#fff' : 'var(--ink-700)',
                cursor: 'pointer',
              }}
            >
              {slot.time}
              <span style={{ opacity: 0.7, marginLeft: 4 }}>
                {t('booking.slots.left', { n: remaining })}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
